/* eslint-disable max-lines-per-function */
import { useHookstate } from '@hookstate/core'
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { ChangeEvent, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import InfiniteScroll from 'react-infinite-scroll-component'
import ErrorFallback from '~/components/ErrorFallback'
import { useLang } from '~/hooks/useLang'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import SearchIcon from '@mui/icons-material/Search'
import { getLastCallsByPhone, getTotalPrankCalls } from './services'
import useEntryState from '../../stores/EntryState'
import dayjs from 'dayjs'
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert'
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt'
import AbcIcon from '@mui/icons-material/Abc'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import { formatPhone } from '~/utils/format'
import useSystemState from '~/features/System/stores/SystemState'
import { Autorenew } from '@mui/icons-material'
import OccurrenceRecallModal from '~/features/Occurrence/components/OccurrenceRecallModal'
import { authFetch } from '~/services/fetch'
import { useTheme } from '~/hooks/useTheme'
import {
  filtersDefault,
  type dispatchDestinationProps,
  type LastCallsListProps,
} from './interfaces'

// eslint-disable-next-line max-lines-per-function
export default function LastCallsList({ phone, totalRows, countPrank }) {
  const { translate } = useLang()
  const lastCallsList = useHookstate<readonly LastCallsListProps[]>([])
  const filters = useHookstate(filtersDefault)
  const entry = useEntryState().entry()
  const { getStatusColors, windowManager, loadingButton } = useSystemState()
  const openOccurrenceRecallModal = useHookstate(false)
  const openRequester = useHookstate(false)
  const occurrenceId = useHookstate(0)
  const rowCount = useHookstate(0)
  const page = useHookstate(0) // current page index used for API
  const hasMoreState = useHookstate(true) // controls infinite scroll
  const dispatchDestination = useHookstate<dispatchDestinationProps | null>(
    null
  )
  const [open, setOpen] = React.useState(false)
  const [code, setCode] = React.useState('')
  const [agency, setAgency] = React.useState('')
  const [dispatchGroup, setDispatchGroup] = React.useState('')
  const [type, setType] = React.useState('')
  const [subtype, setSubtype] = React.useState('')
  const [status, setStatus] = React.useState('')
  const { darkMode } = useTheme()

  const options = [
    {
      id: 1,
      name: 'location',
      buttonName: 'Import Location',
    },
    {
      id: 2,
      name: 'typeOcurrence',
      buttonName: 'Import type/subtype',
    },
    {
      id: 3,
      name: 'requester',
      buttonName: 'Import Requester',
    },
    {
      id: 4,
      name: 'allExports',
      buttonName: 'Import all info',
    },
    {
      id: 5,
      name: 'Recall',
      buttonName: 'Recall',
    },
  ]

  useEffect(() => {
    nextData(true)
  }, [phone])


  const countTotalPranks = () => {
    return getTotalPrankCalls({ phone })
      .then((data) => {
        countPrank.set(data) // Update the Hookstate state
        return data // Optional: return data for the caller
      })
      .catch((error) => {
        console.error('Error in countTotalPranks:', error)
        throw error
      })
  }
  const nextData = async (reset = false) => {
    const pageQuery = reset ? 0 : page.get()
    if (phone?.length === 10 || phone?.length === 11) {
      if (reset) {
        lastCallsList.set([])
        rowCount.set(0)
        page.set(0)
        hasMoreState.set(true)
      }

      await getLastCallsByPhone({
        phone: phone,
        page: pageQuery,
        limit: 10,
        filters: filters.content.get(),
      })
        .then(({ data, rowsCount }) => {
          if (reset) windowManager()['EntryLogModal'].open.set(true)
          if (reset) countTotalPranks()

          if (reset) {
            lastCallsList.set(data)
          } else {
            lastCallsList.merge(data)
          }

          rowCount.set(rowsCount)
          totalRows.set(rowsCount)
          page.set(pageQuery + 1)
          hasMoreState.set(data.length === 10)
        })
        .catch(console.error)
    } else {
      windowManager()['EntryLogModal'].open.set(false)
      countPrank.set(0)
    }
  }

  const actionFilter = () => {
    filters.content.set([])
    Object.keys(filters.fields.value).map((field) => {
      if (
        filters.fields.value[field] != null &&
        filters.fields.value[field] != ''
      ) {
        filters.content.merge([
          {
            field: field,
            operator: 'contains',
            value: filters.fields.value[field],
          },
        ])
      }
    })
    nextData(true)
    filters.hide.set(true)
  }

  const exportData = (dataExport, typeExport: string) => {
    switch (typeExport) {
      case 'location':
        entry.batch((data: typeof entry) => {
          data.location.set(dataExport.location)
          data.address.set(dataExport.address)
          data.latitude.set(dataExport.latitude)
          data.longitude.set(dataExport.longitude)
        })

        break
      case 'typeOcurrence':
        entry.typeId.set(dataExport.type.id)
        entry.subtypeId.set(dataExport.subtype.id)

        break
      case 'requester':
        entry.batch((data: typeof entry) => {
          data.name.set(dataExport.name)
        })
        break

      case 'allExports':
        entry.batch((data: typeof entry) => {
          data.location.set(dataExport.location)
          data.address.set(dataExport.address)
          data.latitude.set(dataExport.latitude)
          data.longitude.set(dataExport.longitude)
          data.name.set(dataExport.name)
        })

        entry.typeId.set(dataExport.type.id)
        entry.subtypeId.set(dataExport.subtype.id)
        break
      default:
    }
  }

  const handleClickOpen = (code) => {
    authFetch({
      url: `/dispatch/get-by-code`,
      method: 'POST',
      data: {
        code: code,
      },
    }).then((response) => {
      dispatchDestination.set(
        response?.data?.unified?.dispatch_destination
          ? response?.data?.unified?.dispatch_destination
          : null
      )
      setCode(code)
      setAgency(` - ${response.data.agency?.name}`)
      setDispatchGroup(`${response.data.dispatch_group?.name}`)
      setType(`${response.data.type?.name}`)
      setSubtype(
        response.data.subtype ? `- ${response.data.subtype?.name}` : ''
      )
      setStatus(`[${response.data.status?.acronym}]`)
      setOpen(true)
    })
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Grid container spacing={2}>
        <Grid container padding={1}>
          <Grid item xs={12} padding={1}>
            <Grid container xs={12} direction="row" spacing={1} padding={0}>
              <Grid item xs={6} spacing={1}>
                <TextField
                  fullWidth
                  label={`${translate('ID')} ${translate('Call')}`}
                  size="small"
                  value={filters.fields.id.get() ?? ''}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    filters.fields.id.set(event.target.value)
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={translate('Requester')}
                  size="small"
                  value={filters.fields.name.get() ?? ''}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    filters.fields.name.set(event.target.value)
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                container // Adiciona container para habilitar o layout flex
                spacing={1}
                display="flex"
                justifyContent="flex-end"
                paddingTop={5}
              >
                <Tooltip title={translate('Filter')} placement="bottom">
                  <IconButton onClick={() => actionFilter()}>
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container padding={1}>
          <Grid item xs={12}>
            <InfiniteScroll
              dataLength={lastCallsList.value?.length}
              next={() => nextData()}
              hasMore={hasMoreState.get()}
              height={335}
              loader={<CircularProgress />}
            >
              {lastCallsList.value.length != 0 ? (
                lastCallsList?.get().map((items: LastCallsListProps) => {
                  return (
                    <>
                      <Divider variant="middle" />
                      <ListItem
                        key={items?.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'start',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: '300px',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              width: '35%',
                              display: 'flex',
                              justifyContent: 'start',
                              right: 50,
                              top: 12,
                              gap: 1,
                            }}
                          >
                            {items?.is_prank_call ? (
                              <>
                                <Typography>
|
</Typography>
                                <Typography
                                  sx={{
                                    background: darkMode
                                      ? '#bf9000'
                                      : '#ffd966',
                                  }}
                                >
                                  {translate('Prank call').toLocaleUpperCase()}
                                </Typography>
                              </>
                            ) : (
                              ''
                            )}
                          </Box>
                          <ListItemText
                            primary={`${
                              dayjs(items?.created_at).isValid()
                                ? dayjs(items?.created_at).format(
                                    'DD/MM/YYYY HH:mm:ss'
                                  )
                                : null
                            }`}
                          />
                          <ListItemText
                            primary={`${
                              items?.name
                                ? items?.name
                                : translate('Uninformed')
                            } - ${phone ? formatPhone(phone) : ''} `}
                          />
                          <ListItemText
                            primary={`${
                              items?.type?.name ? items?.type?.name : ''
                            }  ${
                              items?.subtype?.name
                                ? '- ' + items?.subtype?.name
                                : ''
                            } `}
                          />
                          <ListItemText
                            primary={`${
                              items?.location ? items?.location : ''
                            }`}
                          />
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              flexDirection: 'row',
                              gap: 1,
                            }}
                          >
                            {items?.occurrence
                              ? items?.occurrence?.dispatches?.map(
                                  (element) => (
                                    <Chip
                                      key={element.id}
                                      label={element?.code}
                                      onClick={() =>
                                        handleClickOpen(element.code)
                                      }
                                      sx={{
                                        backgroundColor: getStatusColors(
                                          element.status?.id
                                        ).main,
                                        color: getStatusColors(
                                          element.status?.id
                                        ).contrastText,
                                      }}
                                    />
                                  )
                                )
                              : false}
                          </Box>
                        </Box>

                        <Box>
                          <IconButton
                            title={translate(options[0].buttonName)}
                            onClick={() =>
                              exportData(items ? items : '', options[0].name)
                            }
                          >
                            <AddLocationAltIcon />
                          </IconButton>
                          <IconButton
                            title={translate(options[1].buttonName)}
                            onClick={() =>
                              exportData(items ? items : '', options[1].name)
                            }
                          >
                            <CrisisAlertIcon />
                          </IconButton>
                          <IconButton
                            title={translate(options[2].buttonName)}
                            onClick={() =>
                              exportData(items ? items : '', options[2].name)
                            }
                          >
                            <AbcIcon />
                          </IconButton>
                          <IconButton
                            title={translate(options[3].buttonName)}
                            onClick={() =>
                              exportData(items ? items : '', options[3].name)
                            }
                          >
                            <HowToRegIcon />
                          </IconButton>
                          <IconButton
                            title={translate(options[4].buttonName)}
                            disabled={
                              items?.occurrence?.id === null
                                ? Boolean(
                                    !items?.occurrence?.dispatches.find(
                                      (element) =>
                                        element.status.name === 'ABERTA'
                                    )
                                  )
                                : false
                            }
                            onClick={() => {
                              openOccurrenceRecallModal.set(true),
                                occurrenceId.set(Number(items.occurrence?.id))
                            }}
                          >
                            <Autorenew />
                          </IconButton>
                        </Box>
                      </ListItem>
                      <Divider variant="middle" />
                    </>
                  )
                })
              ) : (
                <Grid
                  xs={12}
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography>
{translate('No Calls')}
</Typography>
                </Grid>
              )}
            </InfiniteScroll>
          </Grid>
        </Grid>
        {openOccurrenceRecallModal.get() ? (
          <OccurrenceRecallModal
            open={openOccurrenceRecallModal}
            openRequester={openRequester}
            occurrenceId={occurrenceId.get()}
            redirectOccurrence={true}
          />
        ) : (
          false
        )}
        <Dialog fullWidth={true} open={open} onClose={handleClose}>
          <DialogTitle>
            {translate('Dispatch')}
{' '}
-
{code}
{' '}
-
{status}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {dispatchGroup} 
{' '}
{agency}
            </Typography>
            <br />
            <Typography>
              {type} 
{' '}
{subtype}
            </Typography>

            {dispatchDestination.get() ? (
              <>
                <br />
                <Typography>
                  {translate('Unified')} 
{' '}
{translate('With')}
:
{' '}
                  <Chip label={dispatchDestination.get()?.code} />
                </Typography>
              </>
            ) : (
              false
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleClose}>
              {translate('Close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </ErrorBoundary>
  )
}
